import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Message,
  User,
  MenuButtonType,
  RoomState,
  GeneralRoomParams,
} from "module/types";
import { Conversation } from "module/chatTypes";
import UserSession from "network/UserSession";
import Chatbox from "components/Chatbox";
import RuleModal from "components/common/RuleModal";
import GameSettingModal from "components/common/GameSettingModal";
import LanguageSwitcher from "localization/LanguageSwitcher";
import { ChatTabs } from "components/chat/ChatTabs";
import { ConversationTargetSelector } from "components/chat/ConversationTargetSelector";
import "sass/views/WaitingRoomView.scss";

interface WaitingRoomViewProps {
  roomParams: GeneralRoomParams;
  userList: User[];
  messages: Message[];
  roomState: RoomState;
  allPlayerReady: boolean;
  aiCount: number;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  onLeaveRoom: (navigatePop: boolean) => void;
  onStartGame: (gameSetting: object) => void;
  onReconnect: () => void;
  // Private chat props (optional)
  conversations?: Conversation[];
  activeTab?: "public" | "private";
  activeChatKey?: string;
  showTargetSelector?: boolean;
  onTabChange?: (tab: "public" | "private") => void;
  onNewConversation?: () => void;
  onConversationSelect?: (conversationId: string) => void;
  onTargetConfirm?: (selectedIds: number[]) => void;
  onTargetCancel?: () => void;
}

const WaitingRoomView: React.FC<WaitingRoomViewProps> = ({
  roomParams,
  userList,
  messages,
  roomState,
  aiCount,
  allPlayerReady,
  setMessages,
  onLeaveRoom,
  onStartGame,
  onReconnect,
  // Private chat props
  conversations,
  activeTab,
  activeChatKey,
  showTargetSelector,
  onTabChange,
  onNewConversation,
  onConversationSelect,
  onTargetConfirm,
  onTargetCancel,
}) => {
  const { t } = useTranslation();
  const [isUserListOpen, setIsUserListOpen] = useState(false);
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isGameSettingOpen, setIsGameSettingOpen] = useState(false);
  const [gameSetting, setGameSetting] = useState<{}>({});

  const toggleMenus = (buttonType: MenuButtonType) => {
    setIsRuleOpen(buttonType === MenuButtonType.RULE ? !isRuleOpen : false);
    setIsUserListOpen(
      buttonType === MenuButtonType.USER_LIST ? !isUserListOpen : false
    );
    setIsGameSettingOpen(
      buttonType === MenuButtonType.GAME_SETTING ? !isGameSettingOpen : false
    );
    setIsMenuOpen(buttonType === MenuButtonType.MENU ? !isMenuOpen : false);
  };

  return (
    <div className="waiting-container space-gray-theme">
      <div className="waiting-header">
        <div className="waiting-header-block">
          <div className="waiting-room-info">
            <div className="waiting-room-info-key">{t("room")}: </div>
            <div className="waiting-room-info-value">
              {roomParams.room.roomName}
            </div>

            {roomParams.room.private && (
              <>
                <div className="waiting-room-info-gap" />
                <div className="waiting-room-info-key">{t("password")}: </div>
                <div className="waiting-room-info-value">
                  {roomParams.room.roomPassword}
                </div>
              </>
            )}
          </div>
          {UserSession.getInstance().user.isHost && roomParams.product.id !== 7 && (
            <button
              onClick={() => toggleMenus(MenuButtonType.GAME_SETTING)}
              className="md:hidden !w-auto text-gray-300 hover:text-white px-1 rounded-full hover:bg-gray-700 transition-colors"
            >
              <i className="fas fa-sliders"></i>
            </button>
          )}
        </div>

        <div className="waiting-title hidden md:block">
          {t(roomParams.product.title + ".title")}
        </div>

        <div className="waiting-header-block gap-1">
          <div className="waiting-title md:hidden">
            {t(roomParams.product.title + ".title")}
          </div>
          <button
            onClick={() => toggleMenus(MenuButtonType.RULE)}
            className="md:block hidden !w-auto h-full text-gray-300 px-2 rounded hover:bg-gray-700 transition-colors"
          >
            <i className="fas fa-book text-xl" />
          </button>
          <button
            onClick={() => toggleMenus(MenuButtonType.USER_LIST)}
            className="md:block hidden !w-auto h-full text-gray-300 px-2 rounded hover:bg-gray-700 transition-colors relative"
          >
            <i className="fas fa-users text-xl" />
            {userList.length > 0 && (
              <div className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold leading-none">
                {userList.length > 99 ? "99+" : userList.length}
              </div>
            )}
          </button>
          {UserSession.getInstance().user.isHost && roomParams.product.id !== 7 && (
            <button
              onClick={() => toggleMenus(MenuButtonType.GAME_SETTING)}
              className="md:block hidden !w-auto h-full text-gray-300 px-2 rounded hover:bg-gray-700 transition-colors"
            >
              <i className="fas fa-sliders text-xl"></i>
            </button>
          )}
          <button
            onClick={() => toggleMenus(MenuButtonType.MENU)}
            className="md:block hidden !w-auto h-full mr-4 text-gray-300 px-2 rounded hover:bg-gray-700 transition-colors"
          >
            <i className="fas fa-cog text-xl"></i>
          </button>
          {/* {roomState === RoomState.InGame ? (
            // not used after changing to Single Page
            <button
              className="waiting-start-button !bg-yellow-500"
              onClick={onReconnect}
            >
              <div className="md:hidden text-sm">{t("reconnect")}</div>
              <div className="md:block hidden">{t("reconnect")}</div>
            </button>
          ) : ( */}
          {UserSession.getInstance().user.isHost && (
            <button
              // TODO: by user ready state
              className={`waiting-start-button ${
                !allPlayerReady ? "disabled" : ""
              }`}
              onClick={() => onStartGame(gameSetting)}
            >
              <i className="fas fa-gamepad md:text-sm text-xs md:mr-2 mr-0" />
              <div className="md:hidden text-sm">{t("start")}</div>
              <div className="md:block hidden">{t("startGame")}</div>
            </button>
          )}
          {/* mobile buttons */}
          <div className="md:hidden relative ml-auto flex gap-1.5">
            <button
              onClick={() => toggleMenus(MenuButtonType.RULE)}
              className="!w-auto text-gray-300 hover:text-white px-1 rounded-full hover:bg-gray-700 transition-colors"
            >
              <i className="fas fa-book"></i>
            </button>
            <button
              onClick={() => toggleMenus(MenuButtonType.USER_LIST)}
              className="!w-auto text-gray-300 hover:text-white px-1 rounded-full hover:bg-gray-700 transition-colors relative"
            >
              <i className="fas fa-users"></i>
              {userList.length > 0 && (
                <div className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold leading-none">
                  {userList.length > 99 ? "99+" : userList.length}
                </div>
              )}
            </button>
            <button
              onClick={() => toggleMenus(MenuButtonType.MENU)}
              className="!w-auto text-gray-300 hover:text-white px-1 rounded-full hover:bg-gray-700 transition-colors"
            >
              <i className="fas fa-cog"></i>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div
            className={`${
              UserSession.getInstance().user.isHost ? "md:right-[180px]" : ""
            } absolute right-0 md:top-[52px] top-[40px] mt-2 w-40 bg-gray-800 rounded-lg shadow-xl z-10 border border-gray-700`}
          >
            <ul className="py-1">
              <li className="px-4 py-2 hover:bg-gray-700 cursor-pointer text-red-400 hover:text-white transition-colors">
                <LanguageSwitcher fromMenu />
              </li>
              <li
                className="px-4 py-2 hover:bg-gray-700 cursor-pointer text-red-400 hover:text-white transition-colors"
                onClick={() => onLeaveRoom(true)}
              >
                <i className="fas fa-sign-out-alt mr-2" />
                {t("leaveRoom")}
              </li>
            </ul>
          </div>
        )}

        <RuleModal
          isOpen={isRuleOpen}
          recreationId={roomParams.product.id}
          inGame={true}
        />
        {roomParams.product.id !== 7 && (
          <GameSettingModal
            isOpen={isGameSettingOpen}
            setGameSetting={setGameSetting}
            roomParams={roomParams}
            aiCount={aiCount}
          />
        )}
      </div>

      {/* Chat Section */}
      <div className="chat-wrapper">
        {/* Chat Tabs (private chat only) */}
        {roomParams.product.attributes.features?.privateChat && (
          <ChatTabs
            activeTab={activeTab || "public"}
            onTabChange={onTabChange || (() => {})}
            unreadPrivateCount={
              conversations?.reduce((sum, c) => sum + c.unreadCount, 0) || 0
            }
            conversations={conversations || []}
            activeChatKey={activeChatKey || null}
            onConversationSelect={onConversationSelect || (() => {})}
            onNewConversation={onNewConversation || (() => {})}
          />
        )}

        {/* Chat Content */}
        <div className="chat-content">
          {/* Chatbox */}
          <Chatbox
            product={roomParams.product}
            roomName={roomParams.room.roomName}
            userList={userList}
            chatList={messages}
            smallChatKey={
              roomParams.product.attributes.features?.privateChat
                ? activeChatKey || ""
                : ""
            }
            isUserListOpen={isUserListOpen}
            setChatList={setMessages}
            setIsUserListOpen={setIsUserListOpen}
          />
        </div>
      </div>

      {/* Target Selector Modal (private chat only) - rendered via Portal */}
      {roomParams.product.attributes.features?.privateChat &&
        showTargetSelector &&
        onTargetConfirm &&
        onTargetCancel &&
        createPortal(
          <ConversationTargetSelector
            availablePlayers={userList}
            currentUserId={UserSession.getInstance().getUserId()}
            onConfirm={onTargetConfirm}
            onCancel={onTargetCancel}
          />,
          document.body
        )}
    </div>
  );
};

export default WaitingRoomView;
